import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector } from '@reduxjs/toolkit';
import type { DocumentationPills, GlobalState, ParsedFieldsByObject } from './globalReducerTypes';
import type { RootState } from '../index';
import type { RelatedObjectsMap } from '../../components/parser/ParserTypes';
import type { ObjectParsedDataByType } from '@server/parser';
import {
  calcConfigurationItemsFromParserResponse,
  calcParsedRecordTypes,
  calcLayouts,
} from '../../components/parser/parserUtils';

type ParserReducersProps = {
  addParsedDataToDocumentation: (
    state: GlobalState,
    action: PayloadAction<{
      response: ObjectParsedDataByType[];
      objectName: string;
      crmOrgId: string;
      fieldsParsedOnDemand: ParsedFieldsByObject;
      pills: DocumentationPills;
    }>,
  ) => void;
};

export const parserReducer: ParserReducersProps = {
  addParsedDataToDocumentation: (state, action) => {
    const { objectName, crmOrgId, response, fieldsParsedOnDemand, pills } = action.payload;
    const newObjectConfigurationItems = calcConfigurationItemsFromParserResponse(response);
    const newObjectRecordTypes = calcParsedRecordTypes(response);
    const newObjectLayouts = calcLayouts(response);
    const oldData = state.environments[crmOrgId]?.data?.documentation ?? {};

    if (state.environments[crmOrgId]?.data?.documentation) {
      state.environments[crmOrgId].data.documentation = {
        ...oldData,
        layouts: { ...oldData.layouts, ...newObjectLayouts },
        configurationItems: [...(oldData.configurationItems ?? []), ...newObjectConfigurationItems],
        parsedRecordTypes: [...(oldData.parsedRecordTypes ?? []), ...newObjectRecordTypes],
        parsedObjectNames: [...(oldData.parsedObjectNames ?? []), objectName],
        parsedFields: { ...(oldData.parsedFields ?? {}), ...fieldsParsedOnDemand },
        pills: { ...(oldData.pills ?? {}), ...pills },
      };
    }
  },
};

export const selectParserInfo = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation;

export const selectParsedFields = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.parsedFields;

export const selectParsedRecordTypes = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.parsedRecordTypes;

export const selectDocumentationConfigurationItems = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.configurationItems;

export const selectParsedObjects = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.parserObjects;

export const selectSaltoObjectTypes = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.saltoObjectTypes;

export const selectonlyActiveConfigurationItems = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.onlyActiveConfigurationItems;

// Cache selector instances per crmOrgId to preserve memoization
const relatedObjectsSelectorsCache = new Map<
  string,
  (state: RootState) => RelatedObjectsMap | undefined
>();

export const selectRelatedObjectsByObjectName = (
  crmOrgId: string,
): ((state: RootState) => RelatedObjectsMap | undefined) => {
  if (!relatedObjectsSelectorsCache.has(crmOrgId)) {
    relatedObjectsSelectorsCache.set(
      crmOrgId,
      createSelector(
        [
          (state: RootState) =>
            state.global.environments[crmOrgId]?.data?.documentation?.relatedObjects,
        ],
        (relatedObjects) => relatedObjects,
      ),
    );
  }
  return relatedObjectsSelectorsCache.get(crmOrgId)!;
};

export const selectLayoutsByObjectName = (crmOrgId: string) => (state: RootState) =>
  state.global.environments[crmOrgId]?.data?.documentation?.layouts;
